import React, { MouseEvent } from 'react';
import cn from 'classnames';
import { IntlShape, useIntl } from 'react-intl';
import LinkToGoogleMap from '../../components/shared/LinkToGoogleMap';
import { textStyles, userCodeStyles } from './TimelineItemBody.styles';
import ImageAdded from './/ImageAdded';
import LinkToClaim from '../shared/LinkToClaim';
import {
  formatEventFuelLevel,
  formatFuelAmount,
  formatLitres,
  getEventFuelTransactionId,
  truncateGeoLocation,
} from '../../utils';
import LinkToFuelTransaction from '../shared/LinkToFuelTransaction';
import LocationListItem from '../shared/LocationListItem';

const formatPumpNumber = (intl: IntlShape, pumpNumber: string) =>
  `${intl.formatMessage({
    id: 'fleetadmin.ryd.pump',
    defaultMessage: 'Pump',
  })} ${pumpNumber}`;

type TextProps = {
  link?: boolean;
  onClick?: (e: MouseEvent) => void;
};

export const Text: React.FC<TextProps> = ({ children, link, onClick }) => (
  <>
    <p className={cn('text', { link })} onClick={onClick}>
      {children}
    </p>
    <style jsx>{textStyles}</style>
  </>
);

type UserCodeProps = {
  userCode: any;
};

export const UserCode: React.FC<UserCodeProps> = ({ userCode }) => (
  <>
    <p>by {userCode}</p>
    <style jsx>{userCodeStyles}</style>
  </>
);

type Props = {
  vehicleEvent: any;
  intl: any;
  onClick?: () => void;
};

export const AreaChanged: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { oldArea, newArea, userReferralCode, principalEmail, comment } = ve;
  return (
    <>
      {newArea && (
        <Text>
          {oldArea} → {newArea}
        </Text>
      )}
      {comment && <p>{`"${comment}"`}</p>}
      {userReferralCode || principalEmail ? (
        <UserCode userCode={userReferralCode || principalEmail} />
      ) : (
        <p>by system</p>
      )}
      <style jsx>{userCodeStyles}</style>
    </>
  );
};

export const BookingAssigned: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const bookingId = ve?.presentableBookingId;
  const { userReferralCode, principalEmail } = ve;

  return (
    <>
      {bookingId && <Text>{bookingId}</Text>}
      {principalEmail || userReferralCode ? (
        <UserCode userCode={principalEmail || userReferralCode} />
      ) : (
        <p>by system</p>
      )}
    </>
  );
};

export const DamageRecordUpdated: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const claimId = ve?.damageId;
  const damageEntityId = ve?.damageEntityId;
  const { mileage, geoLocation } = ve;
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {claimId && <LinkToClaim label={claimId} claimId={damageEntityId} />}
      {mileage != null && <span style={{ marginLeft: 4 }}>| {mileage} km</span>}
    </div>
  );
};

export const CarvalooFeedback: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const intl = useIntl();
  const claimId = ve?.damageId;
  const damageEntityId = ve?.damageEntityId || ve?.damageId;
  const { mileage, geoLocation, eventInfo } = ve;
  const locationLabel = intl.formatMessage({
    id: 'fleetadmin.timeline.location',
    defaultMessage: 'Location',
  });
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {claimId && <LinkToClaim label={claimId} claimId={damageEntityId} />}
        {geoLocation && (
          <span
            style={{ marginLeft: 4, display: 'flex', alignItems: 'center' }}
          >
            | <LinkToGoogleMap position={geoLocation} label={locationLabel} />
          </span>
        )}
        {mileage != null && (
          <span style={{ marginLeft: 4 }}>| {mileage} km</span>
        )}
      </div>
      {ve?.images && ve.images.length > 0 && <ImageAdded vehicleEvent={ve} />}
      {eventInfo && <Text>{`"${eventInfo}"`}</Text>}
    </>
  );
};

export const IgnitionChanged: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { geoLocation, fuelLevel, mileage, connectionType } = ve;
  return connectionType === 'geoTab' ? (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <LinkToGoogleMap position={geoLocation} />
        <span style={{ marginLeft: 4 }}>
          | F {fuelLevel ? `${fuelLevel}%` : '-'}
        </span>
        <span style={{ marginLeft: 4 }}>
          | {mileage ? `${mileage}km` : '-'}
        </span>
      </div>
    </>
  ) : null;
};

export const LockChanged: React.FC<Props> = ({ vehicleEvent: ve }) => {
  return (
    <>
      <UserCode userCode={ve.userReferralCode} />
    </>
  );
};

export const ServiceNoteAdded: React.FC<Props> = ({ vehicleEvent: ve }) => {
  return (
    <>
      {ve?.note && <Text>{`"${ve?.note}"`}</Text>}
      <UserCode userCode={ve.userReferralCode} />
    </>
  );
};

export const ServiceReported: React.FC<Props> = ({ vehicleEvent: ve }) => {
  return (
    <>
      <UserCode userCode={ve.userReferralCode} />
    </>
  );
};

export const ShockDetected: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const intl = useIntl();
  const claimId = ve?.damageEntityId || ve?.damageId;
  const { geoLocation } = ve;
  const locationLabel = intl.formatMessage({
    id: 'fleetadmin.timeline.location',
    defaultMessage: 'Location',
  });

  if (!claimId) {
    return (
      <Text>
        <LinkToGoogleMap position={geoLocation} label={locationLabel} />
      </Text>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <LinkToClaim label={claimId} claimId={claimId} />
      {geoLocation && (
        <span style={{ marginLeft: 4, display: 'flex', alignItems: 'center' }}>
          | <LinkToGoogleMap position={geoLocation} label={locationLabel} />
        </span>
      )}
    </div>
  );
};

export const StatusChanged: React.FC<Props> = ({ vehicleEvent: ve, intl }) => {
  const { oldStatus, newStatus, userReferralCode, principalEmail, comment } =
    ve;

  return (
    <Text>
      {newStatus && (
        <Text>
          {oldStatus} → {newStatus}
        </Text>
      )}
      {comment && <p>{`"${comment}"`}</p>}
      {newStatus === 'BLOCKED' &&
        ve?.blockReasons?.length > 0 &&
        ve.blockReasons.map((reason: string) => (
          <div title={reason} className="tooltip" key={reason}>
            <img
              src={`/images/blocked-reasons/${reason}.svg`}
              alt={reason}
              style={{ marginRight: '12px', width: '14px', height: '14px' }}
            />
          </div>
        ))}
      {principalEmail || userReferralCode ? (
        <UserCode userCode={principalEmail || userReferralCode} />
      ) : (
        <p>by system</p>
      )}
      <style jsx>{userCodeStyles}</style>
    </Text>
  );
};

export const NoteAdded: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { text, author } = ve;

  return (
    <Text>
      {text && <Text>{`"${text}"`}</Text>}
      {author && <UserCode userCode={author} />}
    </Text>
  );
};

export const DocumentAdded: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { text, author, file } = ve;

  return (
    <Text>
      {text && <Text>{`"${text}"`}</Text>}
      {author && <UserCode userCode={author} />}
      {file && (
        <ImageAdded
          vehicleEvent={{
            images: [file],
          }}
        />
      )}
    </Text>
  );
};

export const RecordAdded: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { text } = ve;

  return (
    <>
      <ImageAdded vehicleEvent={ve} />
      <div style={{ marginTop: 8 }} />
      <Text>
        {text && <Text>{`"${text}"`}</Text>}
        <UserCode userCode="GETAWAY Support" />
      </Text>
    </>
  );
};

export const TripReserved: React.FC<Props> = ({ vehicleEvent: ve }) => {
  return (
    <>
      <UserCode userCode={ve.userReferralCode} />
    </>
  );
};

export const Trip: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { geoLocation, fuelLevel, mileage } = ve;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <LinkToGoogleMap position={geoLocation} />
        <span style={{ marginLeft: 4 }}>
          | F {fuelLevel ? `${fuelLevel}%` : '-'}
        </span>
        <span style={{ marginLeft: 4 }}>
          | {mileage ? `${mileage}km` : '-'}
        </span>
      </div>
    </>
  );
};

export const BorderCrossed: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { fromCountry, toCountry, allowed } = ve;
  return (
    <>
      <p>
        <span>
          {` ${fromCountry ? fromCountry : '-'} -> ${
            toCountry ? toCountry : '-'
          } `}
        </span>
        <span>{`${allowed ? '' : '(violation)'}`}</span>
      </p>
    </>
  );
};

export const ImageAddedLink: React.FC<Props> = ({ onClick }) => {
  return (
    <>
      <Text link={true} onClick={onClick}>
        4 photos added
      </Text>
    </>
  );
};

export const TripChecked: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { ownerArea } = ve;
  return (
    <>
      <p>
        <span> {ownerArea ? `${ownerArea}` : '-'}</span>
      </p>
    </>
  );
};

export const MaintenanceClaim: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const intl = useIntl();
  const { note, category } = ve;

  let noteText;
  let characteristic;
  try {
    const parsed = note ? JSON.parse(note) : null;
    if (parsed && typeof parsed === 'object') {
      noteText = parsed.note;
      characteristic = parsed.characteristic;
    }
  } catch {
    // note is a plain string (e.g. older events) — keep it as-is
  }

  return (
    <>
      <ImageAdded vehicleEvent={ve} />
      <div style={{ marginTop: 8 }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        <span>
          {intl.formatMessage({ id: `fleetadmin.timeline.${category}` })} |
        </span>
        {characteristic && <span>{characteristic} |</span>}
        {noteText && <Text> {`"${noteText}"`}</Text>}
      </div>
    </>
  );
};

export const FuelSessionStarted: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const intl = useIntl();
  const { fuelLevel, geoLocation } = ve;
  const fuelTransactionId = getEventFuelTransactionId(ve);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{formatEventFuelLevel(fuelLevel)}</span>
      {fuelTransactionId && (
        <>
          <span>|</span>
          <LinkToFuelTransaction
            fuelTransactionId={fuelTransactionId}
            fuelTransaction={ve.fuelTransaction}
            label={intl.formatMessage({
              id: 'fleetadmin.ryd.session',
              defaultMessage: 'Session',
            })}
          />
        </>
      )}
      {geoLocation && (
        <>
          <span>|</span>
          <LinkToGoogleMap
            position={geoLocation}
            label={truncateGeoLocation(geoLocation)}
          />
        </>
      )}
    </div>
  );
};

export const FuelSessionEnded: React.FC<Props> = ({ vehicleEvent: ve }) => {
  const { fuelLevel, fuelTransaction, fuelledAmount, fuelStationBrand } = ve;
  const fuelQuantity = ve.fuelQuantity ?? fuelTransaction?.quantity;
  const fuelProduct = ve.fuelProduct ?? fuelTransaction?.product;
  const fuelled = [
    fuelQuantity != null ? formatLitres(fuelQuantity) : null,
    fuelProduct,
  ]
    .filter(Boolean)
    .join(' ');
  const row = { display: 'flex', alignItems: 'center', gap: 4 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {fuelStationBrand && (
        <div style={row}>
          <span>{fuelStationBrand}</span>
        </div>
      )}
      <div style={row}>
        <span>{formatEventFuelLevel(fuelLevel)}</span>
        {fuelled && (
          <>
            <span>|</span>
            <span>{fuelled}</span>
          </>
        )}
        {fuelledAmount != null && (
          <>
            <span>|</span>
            <span>{formatFuelAmount(fuelledAmount)}</span>
          </>
        )}
      </div>
    </div>
  );
};

const RydLocationLine: React.FC<{ label: string; position?: string }> = ({
  label,
  position,
}) =>
  position ? (
    <LocationListItem
      label={label}
      position={position}
      style={{ margin: '4px 0 8px' }}
    />
  ) : null;

export const RydFuelSessionStatusChanged: React.FC<Props> = ({
  vehicleEvent: ve,
}) => {
  const intl = useIntl();
  const {
    oldFuelSessionStatus,
    newFuelSessionStatus,
    fuelSessionClosedBy,
    geoLocation,
    fuelLevel,
    mileage,
    pumpNumber,
    fuelTransactionDetails: details,
  } = ve;
  const fuelTransactionId = getEventFuelTransactionId(ve);
  const isIssued = newFuelSessionStatus === 'issued';
  const isCompleted = newFuelSessionStatus === 'completed';
  const carLocation =
    (isIssued
      ? details?.carLocation
      : isCompleted
      ? details?.carLocationAtCompletion
      : undefined) || geoLocation;
  const carMileage =
    (isIssued
      ? details?.mileage
      : isCompleted
      ? details?.mileageAtCompletion
      : undefined) ?? mileage;
  const statusLabel = (status: string) =>
    intl.formatMessage({
      id: `fleetadmin.ryd.fuelSessionStatus.${status}`,
      defaultMessage: status,
    });
  return (
    <>
      {newFuelSessionStatus && (
        <Text>
          {oldFuelSessionStatus
            ? `${statusLabel(oldFuelSessionStatus)} → ${statusLabel(
                newFuelSessionStatus
              )}`
            : statusLabel(newFuelSessionStatus)}
        </Text>
      )}
      {newFuelSessionStatus === 'closed' && fuelSessionClosedBy && (
        <Text>
          {`${intl.formatMessage({
            id: 'fleetadmin.ryd.closedBy',
            defaultMessage: 'closed by',
          })} ${fuelSessionClosedBy}`}
        </Text>
      )}
      {pumpNumber && <Text>{formatPumpNumber(intl, pumpNumber)}</Text>}
      {details && isIssued && (
        <>
          <RydLocationLine
            label={intl.formatMessage({
              id: 'fleetadmin.ryd.stationLocation',
              defaultMessage: 'Station location',
            })}
            position={details.stationLocation}
          />
          <RydLocationLine
            label={intl.formatMessage({
              id: 'fleetadmin.ryd.userLocation',
              defaultMessage: 'Driver location',
            })}
            position={details.userLocation}
          />
        </>
      )}
      {fuelTransactionId && (
        <Text>
          <LinkToFuelTransaction
            fuelTransactionId={fuelTransactionId}
            fuelTransaction={ve.fuelTransaction}
            label={intl.formatMessage({
              id: 'fleetadmin.ryd.fuelTransaction',
              defaultMessage: 'Fuel transaction',
            })}
          />
        </Text>
      )}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {carLocation && <LinkToGoogleMap position={carLocation} />}
        <span style={{ marginLeft: carLocation ? 4 : 0 }}>
          {carLocation ? '| ' : ''}F {fuelLevel != null ? `${fuelLevel}%` : '-'}
        </span>
        <span style={{ marginLeft: 4 }}>
          | {carMileage != null ? `${carMileage}km` : '-'}
        </span>
      </div>
    </>
  );
};
